import React from 'react';
import { CheckCircle } from 'lucide-react';
import { OnboardingContext } from '../../../services/aiIntelligenceService';

interface SummaryStepProps {
  data: OnboardingContext;
}

function SummarySection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4">
      <h4 className="text-sm font-semibold text-gray-700 mb-2">{title}</h4>
      {children}
    </div>
  );
}

function SummaryItem({ label, value }: { label: string; value: string | string[] | number }) {
  const displayValue = Array.isArray(value) ? value.join(', ') : String(value);
  if (!displayValue || displayValue === '0') return null;

  return (
    <div className="flex justify-between items-start py-1">
      <span className="text-xs text-gray-500">{label}</span>
      <span className="text-xs text-gray-900 font-medium text-right max-w-[60%]">{displayValue}</span>
    </div>
  );
}

export function SummaryStep({ data }: SummaryStepProps) {
  return (
    <div>
      <div className="text-center mb-6">
        <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
          <CheckCircle className="w-6 h-6 text-green-600" />
        </div>
        <h2 className="text-xl font-bold text-gray-900 mb-1">Review Your Coaching DNA</h2>
        <p className="text-sm text-gray-600">
          Check your selections below. Click "Confirm & Generate Plan" to submit.
        </p>
      </div>

      <div className="space-y-4">
        {/* Identity */}
        <SummarySection title="Coaching Identity">
          <SummaryItem label="Style" value={data.identity?.coachingStyle || ''} />
          <SummaryItem label="Play Style" value={data.identity?.styleOfPlay || ''} />
          {data.identity?.description && (
            <SummaryItem label="Notes" value={data.identity.description} />
          )}
        </SummarySection>

        {/* Formation */}
        <SummarySection title="Formation">
          <SummaryItem label="Preferred" value={data.formation?.preferred || []} />
          <SummaryItem label="Team Shape" value={data.formation?.teamShape || ''} />
          {data.formation?.notes && (
            <SummaryItem label="Notes" value={data.formation.notes} />
          )}
        </SummarySection>

        {/* In Possession */}
        <SummarySection title="In Possession">
          <SummaryItem label="Philosophy" value={data.inPossession?.philosophy || ''} />
          <SummaryItem label="Build-Up" value={data.inPossession?.buildUpStyle || ''} />
          <SummaryItem label="Pressing Triggers" value={data.inPossession?.pressingTriggers || []} />
        </SummarySection>

        {/* Out of Possession */}
        <SummarySection title="Out of Possession">
          <SummaryItem label="Philosophy" value={data.outOfPossession?.philosophy || ''} />
          <SummaryItem label="Structure" value={data.outOfPossession?.defensiveStructure || ''} />
          <SummaryItem label="Pressing" value={data.outOfPossession?.pressingApproach || ''} />
        </SummarySection>

        {/* Transitions */}
        <SummarySection title="Transitions">
          <SummaryItem label="Attack → Defence" value={data.transitions?.attackToDefence || ''} />
          <SummaryItem label="Defence → Attack" value={data.transitions?.defenceToAttack || ''} />
        </SummarySection>

        {/* Principles */}
        <SummarySection title="Principles">
          <SummaryItem label="Priorities" value={data.principles?.developmentPriorities || []} />
          <SummaryItem label="Objectives" value={data.principles?.coachObjectives || []} />
          {data.principles?.seasonGoals && data.principles.seasonGoals.length > 0 && (
            <SummaryItem label="Season Goals" value={data.principles.seasonGoals} />
          )}
        </SummarySection>

        {/* Training Logistics */}
        <SummarySection title="Training Logistics">
          <SummaryItem label="Age Group" value={data.trainingLogistics?.ageGroup || ''} />
          <SummaryItem label="Level" value={data.trainingLogistics?.competitiveLevel || ''} />
          <SummaryItem label="Days/Week" value={data.trainingLogistics?.daysPerWeek || 0} />
          <SummaryItem label="Duration" value={`${data.trainingLogistics?.sessionDurationMinutes || 0} min`} />
        </SummarySection>
      </div>
    </div>
  );
}
